import apiClient from '../../apiClient';
import { API_ENDPOINTS } from '../../config';

const buildQuery = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, value);
    }
  });
  return query.toString();
};

const handleRequest = async (endpoint, params, signal) => {
  try {
    const query = buildQuery(params);
    const url = `${endpoint}${query ? `?${query}` : ''}`;
    const response = await apiClient.get(url, { signal });
    return {
      success: response.responseCode === 200,
      data: response.data || null,
      error: null
    };
  } catch (error) {
    if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
      return { success: false, data: null, error: null, canceled: true };
    }
    return {
      success: false,
      data: null,
      error: error.response?.data?.responseMessage || 'Failed to fetch financial analytics'
    };
  }
};

const buildFilterParams = ({
  range,
  startDate,
  endDate,
  managerId,
  executiveId,
  topN,
  limit
}) => ({
  range,
  startDate,
  endDate,
  managerId: managerId && managerId !== 'all' ? managerId : undefined,
  executiveId: executiveId && executiveId !== 'all' ? executiveId : undefined,
  topN,
  limit
});

const financialAnalyticsService = {
  getKpis: (params, signal) =>
    handleRequest(API_ENDPOINTS.CRM.FINANCIAL_ANALYTICS.KPIS, buildFilterParams(params), signal),

  getRevenueTrend: (params, signal) =>
    handleRequest(API_ENDPOINTS.CRM.FINANCIAL_ANALYTICS.REVENUE_TREND, buildFilterParams(params), signal),

  getRevenueVsCollections: (params, signal) =>
    handleRequest(API_ENDPOINTS.CRM.FINANCIAL_ANALYTICS.REVENUE_VS_COLLECTIONS, buildFilterParams(params), signal),

  getInvoiceTrend: (params, signal) =>
    handleRequest(API_ENDPOINTS.CRM.FINANCIAL_ANALYTICS.INVOICE_TREND, buildFilterParams(params), signal),

  getInvoiceDistribution: (params, signal) =>
    handleRequest(API_ENDPOINTS.CRM.FINANCIAL_ANALYTICS.INVOICE_DISTRIBUTION, buildFilterParams(params), signal),

  getCollectionTrend: (params, signal) =>
    handleRequest(API_ENDPOINTS.CRM.FINANCIAL_ANALYTICS.COLLECTION_TREND, buildFilterParams(params), signal),

  getPaymentMethods: (params, signal) =>
    handleRequest(API_ENDPOINTS.CRM.FINANCIAL_ANALYTICS.PAYMENT_METHODS, buildFilterParams(params), signal),

  getTopPaymentCustomers: (params, signal) =>
    handleRequest(API_ENDPOINTS.CRM.FINANCIAL_ANALYTICS.TOP_PAYMENT_CUSTOMERS, buildFilterParams(params), signal),

  getOutstandingAging: (params, signal) =>
    handleRequest(API_ENDPOINTS.CRM.FINANCIAL_ANALYTICS.OUTSTANDING_AGING, buildFilterParams(params), signal),

  getTopOutstanding: (params, signal) =>
    handleRequest(API_ENDPOINTS.CRM.FINANCIAL_ANALYTICS.TOP_OUTSTANDING, buildFilterParams(params), signal),

  getTopRevenueCustomers: (params, signal) =>
    handleRequest(API_ENDPOINTS.CRM.FINANCIAL_ANALYTICS.TOP_REVENUE_CUSTOMERS, buildFilterParams(params), signal),

  getCustomerDistribution: (params, signal) =>
    handleRequest(API_ENDPOINTS.CRM.FINANCIAL_ANALYTICS.CUSTOMER_DISTRIBUTION, buildFilterParams(params), signal),

  getMonthlySummary: (params, signal) =>
    handleRequest(API_ENDPOINTS.CRM.FINANCIAL_ANALYTICS.MONTHLY_SUMMARY, buildFilterParams(params), signal),

  getLatestInvoices: (params, signal) =>
    handleRequest(API_ENDPOINTS.CRM.FINANCIAL_ANALYTICS.LATEST_INVOICES, buildFilterParams(params), signal),

  getLatestPayments: (params, signal) =>
    handleRequest(API_ENDPOINTS.CRM.FINANCIAL_ANALYTICS.LATEST_PAYMENTS, buildFilterParams(params), signal),

  getLargestPayments: (params, signal) =>
    handleRequest(API_ENDPOINTS.CRM.FINANCIAL_ANALYTICS.LARGEST_PAYMENTS, buildFilterParams(params), signal),

  getExecutiveSummary: (params, signal) =>
    handleRequest(API_ENDPOINTS.CRM.FINANCIAL_ANALYTICS.EXECUTIVE_SUMMARY, buildFilterParams(params), signal)
};

export default financialAnalyticsService;
